import { FLOW_MAX_GOALS_MAX, FLOW_MAX_GOALS_MIN } from "@repo/shared";

// Local translations
export const T = {
  en_GB: {
    qrCode: "Show QR code",
    // Flow Information
    flowInformation: "Flow Information",
    flowInformationDescription: "Basic information about this flow",
    name: "Name",
    clientOnboardingPlaceholder: "e.g., Client Onboarding",
    description: "Description",
    describePurpose: "Describe the purpose of this flow",
    currency: "Currency",
    currencyDescription:
      "Currency for portfolio calculations and historical returns. Set when the flow is created and can't be changed.",
    allowedClientTypes: "Allowed client types",
    allowedClientTypesDescription:
      "Which client types can start this flow. At least one must stay selected.",
    clientTypePerson: "Person",
    clientTypeCompany: "Company",
    clientTypeGroup: "Group",
    // Risk Assessment
    riskAssessmentConfig: "Risk Assessment Configuration",
    riskAssessmentConfigDescription: "Configure risk model and portfolio collection for this flow",
    riskModel: "Risk Model",
    selectRiskModel: "Select risk model",
    noRiskModel: "No risk model",
    requiredForRiskAndPortfolio: "Required for risk assessment and portfolio features",
    portfolioCollection: "Portfolio Collection",
    selectPortfolioCollection: "Select portfolio collection",
    noPortfolioCollection: "No portfolio collection",
    selectRiskModelFirst: "Select a risk model first",
    requiredForPortfolioDisplay: "Required for portfolio display and X-ray features",
    // QL-372 — the dropdown only lists collections in the flow currency
    collectionsInCurrency: (code: string) => `Only collections in ${code} are listed.`,
    // Financial Situation
    financialSituationAssessment: "Financial Situation Assessment",
    financialSituationAssessmentDescription:
      "Configure the financial situation model for MiFID II assessments",
    financialSituationModel: "Financial Situation Model",
    fsModelBoundByCollection: (model: string) =>
      `This collection's platform rules require "${model}" — the model is selected automatically.`,
    noModel: "No model",
    selectModel: "Select model",
    requiredForFinSitComponents:
      "Required for Financial Situation components. Defines income, asset, liability, and commitment fields.",
    // Multi-Goal
    multiGoalConfig: "Multi-Goal Configuration",
    multiGoalConfigDescription: "Enable multiple investment goals per flow instance",
    enableMultiGoal: "Enable Multi-Goal",
    enableMultiGoalDescription:
      "Allow creating multiple goals with different schedules, risk profiles, and portfolios",
    maxGoals: "Maximum Goals",
    maxGoalsDescription: `How many goals one instance of this flow can hold (${FLOW_MAX_GOALS_MIN}–${FLOW_MAX_GOALS_MAX}). Lowering it never removes existing goals; it only stops new ones being added.`,
    maxGoalsInvalid: `Maximum goals must be a whole number from ${FLOW_MAX_GOALS_MIN} to ${FLOW_MAX_GOALS_MAX}`,
    // Multi-Portfolio
    multiPortfolioConfig: "Multi-Portfolio Configuration",
    multiPortfolioConfigDescription: "Enable multiple portfolios per goal",
    enableMultiPortfolio: "Enable Multi-Portfolio",
    enableMultiPortfolioDescription:
      "Allow each goal to hold multiple portfolios. Cannot be turned off once instances of this flow exist — clone the flow instead.",
    // Process
    processTitle: "Process",
    processDescription: "Which steps run after a flow instance is submitted",
    useComplianceApproval: "Use Compliance Approval",
    useComplianceApprovalDescription:
      "Which flows a compliance user must approve before they can continue to the next step",
    complianceApprovalNone: "No",
    complianceApprovalAll: "All flows",
    complianceApprovalHits: "PEP / Sanction hits",
    useExternalApproval: "Use External Status",
    useExternalApprovalDescription:
      "An external system must approve the flow via the API before it can continue",
    useEidSigning: "Use eID Signing",
    useEidSigningDescription: "Allow signing with national eID (BankID, MitID, …)",
    useDigitalSigning: "Use Digital Signing",
    useDigitalSigningDescription: "Allow simple drawn signatures",
    autoGeneratePdfOnSubmit: "Generate PDF automatically on submit",
    autoGeneratePdfOnSubmitDescription:
      "Render the PDF when a flow instance is submitted and continue the process without the advisor pressing Generate PDF",
    autoGeneratePdfNoTemplate: "Requires an active PDF template for this flow",
    cancelReasonLabel: "Cancellation Reason",
    cancelReasonDescription: "Ask for a reason when an advice is cancelled",
    cancelReasonHidden: "Hidden",
    cancelReasonOptional: "Optional",
    cancelReasonRequired: "Required",
    publicSigningBlockedByGates:
      "Disabled while compliance or external approval is enabled — those steps run on the advisor side",
    // Public Access
    publicAccess: "Public Access",
    publicAccessDescription: "Allow clients to start new flow instances from a public link",
    allowPublicStart: "Allow Public Start",
    allowPublicStartDescription:
      "Clients can create their own flow instance by visiting a public link",
    noPublicPagesWarning:
      "This flow has no public pages. Add at least one public page before clients can use the public start link.",
    publicStartLink: "Public Start Link",
    copy: "Copy",
    sharePublicLinkDescription:
      "Share this link with clients to let them start a new flow instance",
    completionPageOptions: "Completion Page Options",
    allowPdfGeneration: "Allow PDF Generation",
    allowPdfGenerationDescription: "Client can generate a PDF summary after submitting",
    allowDocumentSigning: "Allow Document Signing",
    allowDocumentSigningDescription: "Client can sign the generated PDF on the completion page",
    enablePdfFirst: "Enable PDF generation first to allow signing",
    // Flow Status
    flowStatus: "Flow Status",
    flowStatusDescription: "Control who can start and submit instances of this flow",
    statusInactiveHint:
      "Nobody can start new instances. Existing instances can still be opened and submitted.",
    statusPreviewHint:
      "Only designers can start and submit instances, in the app — advisors, clients and the public API cannot. Use it to try the flow out before it goes live.",
    statusActiveHint: "Everyone can start and submit instances.",
    active: "Active",
    publicPages: "public",
    internalPages: "internal",
    pages: "pages",
    emptyPagesWarning: "Some pages have no components. Add components to activate this flow.",
    noPortfolioPageWarning:
      "This flow has a portfolio collection but no portfolio page. Add a portfolio page (in the Pages tab) to collect portfolio inputs and show portfolio analytics.",
    // Followup
    followupTitle: "Followup",
    followupDescription: "Control whether advisors can start followups with this flow",
    supportedForFollowup: "Supported for followup",
    supportedForFollowupDescription:
      "Advisors can start followups of existing accounts and agreements with this flow",
    followupRequiresActive:
      "Set the flow to Active first — only active flows can support followups.",
    followupRequiresCollection: "Select a portfolio collection first — followups require one.",
    followupShapeNote:
      "Without multiple goals/portfolios this flow only supports single-account followups.",
    cannotChangeFollowupSupport: "Cannot Change Followup Support",
    // Actions
    reset: "Reset",
    save: "Save",
    // Dialogs
    validationError: "Validation Error",
    flowNameIsRequired: "Flow name is required",
    ok: "OK",
    error: "Error",
    failedToSaveFlow: "Failed to save flow",
    cannotChangeFlowStatus: "Cannot Change Flow Status",
  },
  nb_NO: {
    qrCode: "Vis QR-kode",
    // Flow Information
    flowInformation: "Flytinformasjon",
    flowInformationDescription: "Grunnleggende informasjon om denne flyten",
    name: "Navn",
    clientOnboardingPlaceholder: "f.eks. Kunde-onboarding",
    description: "Beskrivelse",
    describePurpose: "Beskriv formålet med denne flyten",
    currency: "Valuta",
    currencyDescription:
      "Valuta for porteføljeberegninger og historisk avkastning. Settes når flyten opprettes og kan ikke endres.",
    allowedClientTypes: "Tillatte kundetyper",
    allowedClientTypesDescription:
      "Hvilke kundetyper som kan starte denne flyten. Minst én må være valgt.",
    clientTypePerson: "Person",
    clientTypeCompany: "Selskap",
    clientTypeGroup: "Gruppe",
    // Risk Assessment
    riskAssessmentConfig: "Risikovurderingskonfigurasjon",
    riskAssessmentConfigDescription:
      "Konfigurer risikomodell og porteføljesamling for denne flyten",
    riskModel: "Risikomodell",
    selectRiskModel: "Velg risikomodell",
    noRiskModel: "Ingen risikomodell",
    requiredForRiskAndPortfolio: "Påkrevd for risikovurdering og porteføljefunksjoner",
    portfolioCollection: "Porteføljesamling",
    selectPortfolioCollection: "Velg porteføljesamling",
    noPortfolioCollection: "Ingen porteføljesamling",
    selectRiskModelFirst: "Velg en risikomodell først",
    requiredForPortfolioDisplay: "Påkrevd for porteføljevisning og X-ray-funksjoner",
    collectionsInCurrency: (code: string) => `Bare samlinger i ${code} vises.`,
    // Financial Situation
    financialSituationAssessment: "Økonomisk situasjonsvurdering",
    financialSituationAssessmentDescription:
      "Konfigurer den økonomiske situasjonsmodellen for MiFID II-vurderinger",
    financialSituationModel: "Økonomisk situasjonsmodell",
    fsModelBoundByCollection: (model: string) =>
      `Denne samlingens plattformregler krever «${model}» — modellen velges automatisk.`,
    noModel: "Ingen modell",
    selectModel: "Velg modell",
    requiredForFinSitComponents:
      "Påkrevd for økonomisk situasjonskomponenter. Definerer inntekts-, eiendels-, gjelds- og forpliktelsesfelt.",
    // Multi-Goal
    multiGoalConfig: "Fler-mål-konfigurasjon",
    multiGoalConfigDescription: "Aktiver flere investeringsmål per flytforekomst",
    enableMultiGoal: "Aktiver fler-mål",
    enableMultiGoalDescription:
      "Tillat opprettelse av flere mål med forskjellige tidsplaner, risikoprofiler og porteføljer",
    maxGoals: "Maksimalt antall mål",
    maxGoalsDescription: `Hvor mange mål én forekomst av denne flyten kan ha (${FLOW_MAX_GOALS_MIN}–${FLOW_MAX_GOALS_MAX}). Å senke tallet fjerner aldri eksisterende mål; det hindrer bare at nye legges til.`,
    maxGoalsInvalid: `Maksimalt antall mål må være et heltall fra ${FLOW_MAX_GOALS_MIN} til ${FLOW_MAX_GOALS_MAX}`,
    // Multi-Portfolio
    multiPortfolioConfig: "Fler-portefølje-konfigurasjon",
    multiPortfolioConfigDescription: "Aktiver flere porteføljer per mål",
    enableMultiPortfolio: "Aktiver fler-portefølje",
    enableMultiPortfolioDescription:
      "Tillat at hvert mål kan ha flere porteføljer. Kan ikke slås av når det finnes forekomster av denne flyten — klon flyten i stedet.",
    // Process
    processTitle: "Prosess",
    processDescription: "Hvilke trinn som kjøres etter at en flytinstans er sendt inn",
    useComplianceApproval: "Bruk compliance-godkjenning",
    useComplianceApprovalDescription:
      "Hvilke flyter en compliance-bruker må godkjenne før de kan fortsette til neste trinn",
    complianceApprovalNone: "Nei",
    complianceApprovalAll: "Alle flyter",
    complianceApprovalHits: "PEP-/sanksjonstreff",
    useExternalApproval: "Bruk ekstern status",
    useExternalApprovalDescription:
      "Et eksternt system må godkjenne flyten via API-et før den kan fortsette",
    useEidSigning: "Bruk eID-signering",
    useEidSigningDescription: "Tillat signering med nasjonal eID (BankID, MitID, …)",
    useDigitalSigning: "Bruk digital signering",
    useDigitalSigningDescription: "Tillat enkle tegnede signaturer",
    autoGeneratePdfOnSubmit: "Generer PDF automatisk ved innsending",
    autoGeneratePdfOnSubmitDescription:
      "Generer PDF-en når en flytinstans sendes inn, og fortsett prosessen uten at rådgiveren trykker Generer PDF",
    autoGeneratePdfNoTemplate: "Krever en aktiv PDF-mal for denne flyten",
    cancelReasonLabel: "Kanselleringsårsak",
    cancelReasonDescription: "Be om en årsak når et råd kanselleres",
    cancelReasonHidden: "Skjult",
    cancelReasonOptional: "Valgfri",
    cancelReasonRequired: "Påkrevd",
    publicSigningBlockedByGates:
      "Deaktivert mens compliance eller ekstern godkjenning er aktivert — disse trinnene kjøres på rådgiversiden",
    // Public Access
    publicAccess: "Offentlig tilgang",
    publicAccessDescription: "Tillat kunder å starte nye flytforekomster fra en offentlig lenke",
    allowPublicStart: "Tillat offentlig start",
    allowPublicStartDescription:
      "Kunder kan opprette sin egen flytforekomst ved å besøke en offentlig lenke",
    noPublicPagesWarning:
      "Denne flyten har ingen offentlige sider. Legg til minst én offentlig side før kunder kan bruke den offentlige startlenken.",
    publicStartLink: "Offentlig startlenke",
    copy: "Kopier",
    sharePublicLinkDescription:
      "Del denne lenken med kunder for å la dem starte en ny flytforekomst",
    completionPageOptions: "Fullføringsside-alternativer",
    allowPdfGeneration: "Tillat PDF-generering",
    allowPdfGenerationDescription: "Kunden kan generere en PDF-oppsummering etter innsending",
    allowDocumentSigning: "Tillat dokumentsignering",
    allowDocumentSigningDescription: "Kunden kan signere den genererte PDF-en på fullføringssiden",
    enablePdfFirst: "Aktiver PDF-generering først for å tillate signering",
    // Flow Status
    flowStatus: "Flytstatus",
    flowStatusDescription:
      "Kontroller hvem som kan starte og sende inn forekomster av denne flyten",
    statusInactiveHint:
      "Ingen kan starte nye forekomster. Eksisterende forekomster kan fortsatt åpnes og sendes inn.",
    statusPreviewHint:
      "Bare designere kan starte og sende inn forekomster, i appen — rådgivere, kunder og det offentlige API-et kan ikke. Bruk det til å prøve flyten før den går live.",
    statusActiveHint: "Alle kan starte og sende inn forekomster.",
    active: "Aktiv",
    publicPages: "offentlige",
    internalPages: "interne",
    pages: "sider",
    emptyPagesWarning:
      "Noen sider har ingen komponenter. Legg til komponenter for å aktivere denne flyten.",
    noPortfolioPageWarning:
      "Denne flyten har en porteføljesamling, men ingen porteføljeside. Legg til en porteføljeside (i Sider-fanen) for å samle porteføljeinndata og vise porteføljeanalyser.",
    // Followup
    followupTitle: "Oppfølging",
    followupDescription: "Kontroller om rådgivere kan starte oppfølginger med denne flyten",
    supportedForFollowup: "Støttet for oppfølging",
    supportedForFollowupDescription:
      "Rådgivere kan starte oppfølginger av eksisterende kontoer og avtaler med denne flyten",
    followupRequiresActive:
      "Sett flyten til Aktiv først — bare aktive flyter kan støtte oppfølginger.",
    followupRequiresCollection: "Velg en porteføljesamling først — oppfølginger krever en.",
    followupShapeNote:
      "Uten flere mål/porteføljer støtter denne flyten bare oppfølginger av én konto.",
    cannotChangeFollowupSupport: "Kan ikke endre oppfølgingsstøtte",
    // Actions
    reset: "Tilbakestill",
    save: "Lagre",
    // Dialogs
    validationError: "Valideringsfeil",
    flowNameIsRequired: "Flytnavn er påkrevd",
    ok: "OK",
    error: "Feil",
    failedToSaveFlow: "Kunne ikke lagre flyt",
    cannotChangeFlowStatus: "Kan ikke endre flytstatus",
  },
} as const;
